/**
 * Size guide pop-up — fills a dialog from the Section Rendering API.
 *
 * Markup: blocks/ll-size-guide.liquid. The trigger is a link to the Sizes
 * page carrying `data-ll-size-guide-trigger`, `data-section-id` (the Custom
 * HTML section on that page) and `aria-controls` (the block's `<dialog>`,
 * inside a stock `<dialog-component>`). On click this asks for that one section,
 * `{href}?section_id={id}`, lifts the `[data-ll-custom-html]` div out of the
 * response and shows it in the dialog.
 * https://shopify.dev/docs/api/ajax/section-rendering
 *
 * Responses are cached per URL for the visit and warmed on hover or focus,
 * so the second open, and usually the first, is instant. Anything that goes
 * wrong (no section id, 404, empty section, network) lets the link do what
 * a link does and open the page.
 *
 * Delegated on `document`: the product details column is re-rendered on
 * variant change, which would drop listeners bound to the link itself.
 */

const TRIGGER = '[data-ll-size-guide-trigger]';
const SOURCE = '[data-ll-custom-html]';
const CONTENT = '[data-ll-size-guide-content]';

/** @type {Map<string, Promise<Element>>} */
const cache = new Map();

/**
 * @param {Element} trigger
 * @returns {string | null} the Section Rendering API URL, or null if the trigger is not set up.
 */
function sectionUrl(trigger) {
  const href = trigger.getAttribute('href');
  const sectionId = trigger.getAttribute('data-section-id')?.trim();
  if (!href || !sectionId) return null;

  const url = new URL(href, window.location.origin);
  url.search = '';
  url.searchParams.set('section_id', sectionId);
  return url.toString();
}

/**
 * Fetches the section once and resolves with the Custom HTML div.
 * @param {string} url
 * @returns {Promise<Element>}
 */
function load(url) {
  const cached = cache.get(url);
  if (cached) return cached;

  const request = fetch(url, { headers: { Accept: 'text/html' } })
    .then((response) => {
      if (!response.ok) throw new Error(`Size guide section ${response.status}`);
      return response.text();
    })
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector(SOURCE);
      if (!source || !source.textContent?.trim()) throw new Error('Size guide section is empty');
      return source;
    });

  // A failed request must not poison the cache: the next click retries.
  request.catch(() => cache.delete(url));
  cache.set(url, request);
  return request;
}

/**
 * @param {Element} trigger
 */
function warm(trigger) {
  const url = sectionUrl(trigger);
  if (url) load(url).catch(() => {});
}

/**
 * @param {HTMLAnchorElement} trigger
 * @param {any} dialog - the block's dialog-component
 * @param {string} url
 */
async function open(trigger, dialog, url) {
  const content = dialog.querySelector(CONTENT);
  if (!content) return;

  trigger.setAttribute('aria-busy', 'true');
  try {
    const source = await load(url);
    // Section re-renders can reset the dialog, so always fill from the cached node.
    content.replaceChildren(source.cloneNode(true));
    dialog.showDialog();
  } catch {
    window.location.assign(trigger.href);
  } finally {
    trigger.removeAttribute('aria-busy');
  }
}

/**
 * @param {MouseEvent} event
 */
function handleClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest(TRIGGER);
  if (!(trigger instanceof HTMLAnchorElement)) return;

  // Modifier clicks open the page in a new tab, as the visitor intends.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const url = sectionUrl(trigger);
  const dialogId = trigger.getAttribute('aria-controls');
  const dialog = dialogId ? document.getElementById(dialogId)?.closest('dialog-component') : null;
  if (!url || !dialog || typeof (/** @type {any} */ (dialog).showDialog) !== 'function') return;

  event.preventDefault();
  open(trigger, dialog, url);
}

/**
 * @param {Event} event
 */
function handleWarm(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const trigger = target.closest(TRIGGER);
  if (trigger) warm(trigger);
}

if (!window.__llSizeGuideBound) {
  window.__llSizeGuideBound = true;
  document.addEventListener('click', handleClick);
  document.addEventListener('pointerenter', handleWarm, true);
  document.addEventListener('focusin', handleWarm);
}
